import { Select } from "@/components/ui/select"
import { Combobox, ComboboxItem, ComboboxList, ComboboxProvider } from "@ariakit/react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { CheckIcon, SearchIcon } from "lucide-react"
import { matchSorter } from "match-sorter"
import {
  FormFieldWrapper,
  FormFieldWrapperProps,
  inputIconVariants,
  inputVariants,
  useFieldContext,
} from "."
import { extractInputProps, extractWrapperProps } from "./field-wrapper"

/**
 * FormSelect
 */
type Props = SelectInputProps & FormFieldWrapperProps
export const FormSelectSearch: React.FC<Props> = ({ options, ...props }) => (
  <FormFieldWrapper {...extractWrapperProps(props)}>
    <SelectInput {...extractInputProps(props)} options={options} />
  </FormFieldWrapper>
)

type SelectInputProps = {
  placeholder?: string
  searchPlaceholder?: string
  options: FormSelectSearchOption[]
  className?: ClassName
  disabled?: boolean
}
const SelectInput: React.FC<SelectInputProps> = ({
  className,
  placeholder,
  searchPlaceholder = "search",
  disabled,
  options,
}) => {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const { value, setFieldValue, disabled: ctxDisabled } = useFieldContext<string>()
  const matches = React.useMemo(() => {
    if (!searchValue) return options
    const matches = matchSorter(options, searchValue, {
      keys: [({ search, label }) => search ?? label, D.getUnsafe("value")],
    })
    const selected = A.find(options, option => option.value === value)
    if (selected && !matches.includes(selected)) [selected, matches]
    return matches
  }, [searchValue, value, options])
  const selected = React.useMemo(
    () => A.find(matches, option => option.value === value),
    [matches, value]
  )
  return (
    <Select
      open={open}
      onOpenChange={setOpen}
      onValueChange={setFieldValue}
      defaultValue={value}
      disabled={ctxDisabled || disabled}
    >
      <ComboboxProvider
        open={open}
        setOpen={setOpen}
        resetValueOnHide
        includesBaseElement={false}
        setValue={value => {
          React.startTransition(() => {
            setSearchValue(value)
          })
        }}
      >
        <Select.Trigger className={cxm("w-full", className)}>
          {selected?.label || placeholder}
          {/* <Select.Value placeholder={placeholder}></Select.Value> */}
        </Select.Trigger>
        <Select.Content>
          <div className="sticky top-0 z-20 bg-popover">
            <div className="relative m-2">
              <span className={inputIconVariants({ size: "xs", side: "left" })}>
                <SearchIcon className="size-4" />
              </span>
              <Combobox
                autoSelect
                placeholder={searchPlaceholder}
                className={inputVariants({
                  size: "xs",
                  icon: "left",
                  className: "focus-visible:ring-0",
                })}
                onBlurCapture={event => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              />
            </div>
          </div>
          <ComboboxList className="listbox">
            {A.mapWithIndex(A.take(matches, 100), (index, option) => (
              <SelectPrimitive.Item key={index} value={option.value} asChild>
                <ComboboxItem
                  className={cx(
                    "relative flex items-center w-full py-1.5 pl-8 pr-2 cursor-default select-none rounded-sm text-sm outline-none",
                    "data-[active-item]:bg-accent data-[active-item]:text-accent-foreground",
                    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                  )}
                >
                  <SelectPrimitive.ItemText>
                    {option.labelNode ?? option.label}
                  </SelectPrimitive.ItemText>
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <CheckIcon className="size-4" aria-hidden />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                </ComboboxItem>
              </SelectPrimitive.Item>
            ))}
          </ComboboxList>
        </Select.Content>
      </ComboboxProvider>
    </Select>
  )
}

/**
 * helpers
 */
export const toSelectOptions = (options: string[], _: (t: string) => string, prefix: string) =>
  A.map(options, o => ({ label: _(`${prefix}-${o}`), value: o }))

/**
 * types
 */
export type FormSelectSearchOption = React.ComponentPropsWithoutRef<typeof Select.Item> & {
  label: string
  labelNode?: React.ReactNode
  search?: string
}
